/**
 * REVENUE BAND CONSTANTS - ENFORCES BUSINESS RULES
 * 
 * Only 3 revenue bands are supported as per business requirements.
 * Any code referencing other bands must be updated to use these exact values.
 */

export const VALID_REVENUE_BANDS = [
  'Emerging',
  'Enterprise', 
  'Large Enterprise'
] as const;

export type ValidRevenueBand = typeof VALID_REVENUE_BANDS[number];

export const REVENUE_BAND_DETAILS = {
  'Emerging': {
    label: 'Emerging',
    range: '$0 – $5B',
    description: 'Companies with revenue up to $5 billion'
  },
  'Enterprise': {
    label: 'Enterprise', 
    range: '$5B – $25B',
    description: 'Companies with revenue between $5-25 billion'
  },
  'Large Enterprise': {
    label: 'Large Enterprise',
    range: '$25B+', 
    description: 'Companies with revenue over $25 billion'
  }
} as const;

/**
 * Validate if a revenue band is supported
 */
export function isValidRevenueBand(band: string): band is ValidRevenueBand {
  return VALID_REVENUE_BANDS.includes(band as ValidRevenueBand);
}

/**
 * Normalize legacy revenue bands to valid ones
 */
export function normalizeRevenueBand(band: string): ValidRevenueBand {
  const normalized = band.toLowerCase().trim();
  
  // Legacy mappings
  if (normalized.includes('emerging') || normalized.includes('0') || normalized.includes('5b')) {
    return 'Emerging';
  }
  if (normalized.includes('enterprise') || normalized.includes('5b') || normalized.includes('25b')) {
    return 'Enterprise';
  }
  if (normalized.includes('large') || normalized.includes('25b+') || normalized.includes('>25b')) {
    return 'Large Enterprise';
  }
  
  // Default fallback
  console.warn(`Unknown revenue band "${band}", defaulting to Enterprise`);
  return 'Enterprise';
}